/**
 * Types et constantes du pipeline de leads, partagés entre le serveur
 * (lib/leads.ts) et les composants client. Ce module ne doit importer
 * aucune dépendance serveur (db, logger…).
 */

import { DEFAULT_LOCALE, messages, type Locale } from "@shared/i18n";

export const LEAD_STATUSES = ["new", "to_contact", "contacted", "replied", "won", "dismissed"] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Label of a pipeline status in a language (French by default: admin, agent prompts); an unknown value shown as is. */
export function leadStatusLabel(status: LeadStatus, locale: Locale = DEFAULT_LOCALE): string {
  const labels: Partial<Record<string, string>> = messages(locale).leads.statuses;
  return labels[status] ?? status;
}

/** French labels of the statuses, for French-only callers (admin, agent prompts). */
export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = messages(DEFAULT_LOCALE).leads.statuses;

/**
 * Raisons d'écartement. Fermée volontairement : une raison libre ne s'agrège
 * pas, et c'est l'agrégation qui permet à l'agent d'apprendre. `feedsIcp`
 * marque celles qui disent « ce n'est pas ma cible » — les seules qui doivent
 * remonter dans les exclusions de l'ICP (un mauvais timing n'exclut personne).
 * Labels and hints live in the dictionary (`leads.dismissReasons`).
 */
export const DISMISS_REASONS = [
  { key: "off_target", feedsIcp: true },
  { key: "wrong_size", feedsIcp: true },
  { key: "competitor", feedsIcp: true },
  { key: "already_customer", feedsIcp: true },
  { key: "signal_weak", feedsIcp: false },
  { key: "no_contact", feedsIcp: false },
  { key: "bad_timing", feedsIcp: false },
  { key: "other", feedsIcp: false },
] as const;

export type DismissReason = (typeof DISMISS_REASONS)[number]["key"];

/** Label and hint of each dismissal reason in a language (French by default). */
export function dismissReasonCopy(locale: Locale = DEFAULT_LOCALE): Record<DismissReason, { label: string; hint: string }> {
  return messages(locale).leads.dismissReasons;
}

/** Label of a dismissal reason in a language (French by default: agent memory, admin); an unknown value shown as is. */
export function dismissReasonLabel(reason: DismissReason, locale: Locale = DEFAULT_LOCALE): string {
  const copy: Partial<Record<string, { label: string }>> = dismissReasonCopy(locale);
  return copy[reason]?.label ?? reason;
}

/** French labels of the dismissal reasons, for French-only callers (agent memory). */
export const DISMISS_REASON_LABELS = Object.fromEntries(
  DISMISS_REASONS.map((reason) => [reason.key, dismissReasonLabel(reason.key)]),
) as Record<DismissReason, string>;

export function isDismissReason(value: unknown): value is DismissReason {
  return typeof value === "string" && DISMISS_REASONS.some((reason) => reason.key === value);
}

/** Les raisons qui remontent dans l'ICP : l'agent doit cesser de proposer ça. */
export function feedsIcp(reason: DismissReason) {
  return DISMISS_REASONS.find((item) => item.key === reason)?.feedsIcp ?? false;
}

export type LeadAssessment = {
  need_intensity: number | null;
  solution_alignment: number | null;
  decision_window: number | null;
  contact_path: number | null;
  source_confidence: number | null;
};

/** Les 5 axes du score détaillé (ordre d'affichage) ; labels in `leads.assessmentAxes`. */
export const ASSESSMENT_AXES: ReadonlyArray<keyof LeadAssessment> = [
  "need_intensity",
  "solution_alignment",
  "decision_window",
  "contact_path",
  "source_confidence",
];

/**
 * Label of a signal's readiness. The value is stored in French whatever the
 * language (`report.ts`): it is translated here, an unknown value shown as is.
 */
export function readinessLabel(readiness: string, locale: Locale = DEFAULT_LOCALE): string {
  return messages(locale).leads.readiness[readiness] ?? readiness;
}

export type LeadSignal = {
  market_clue: string;
  readiness: string | null;
  priority: number | null;
  category: string | null;
  source_name: string | null;
  source_link: string;
  /** Phrase exacte de la source qui fonde le signal (absente des passages anciens). */
  source_quote: string | null;
  observed_on: string | null;
  contact_route: string | null;
  message_draft: string | null;
  relevance: string | null;
  momentum: string | null;
  verification_note: string | null;
  assessment: LeadAssessment | null;
  created_at: string;
};

/** Page publique où l'enrichissement a trouvé l'interlocuteur. */
export type ContactSource = {
  title: string | null;
  url: string;
  date: string | null;
  supports?: string[];
};

export type ContactCandidate = {
  id: string;
  organization_id: string;
  priority_rank: number;
  contact_name: string;
  contact_role: string | null;
  decision_role:
    | "economic_buyer"
    | "final_decision_maker"
    | "business_owner"
    | "operational_owner"
    | "influencer"
    | "executive_fallback"
    | null;
  fit_score: number | null;
  confidence: "high" | "medium" | "low" | null;
  contact_linkedin_url: string | null;
  contact_photo_url: string | null;
  public_professional_email: string | null;
  contact_reason: string | null;
  responsibilities: string[];
  relevant_background: string[];
  recent_professional_signals: Array<{ fact: string; date: string | null; source_url: string }>;
  personalization_facts: Array<{ fact: string; why_relevant: string; source_url: string }>;
  professional_summary: string | null;
  public_profile_links: Array<{
    kind: "linkedin_profile" | "linkedin_post" | "interview" | "article" | "podcast" | "conference" | "social_post" | "other";
    title: string;
    url: string;
    description: string | null;
    date: string | null;
  }>;
  icebreakers: Array<{
    text: string;
    why_it_works: string;
    source_url: string;
    confidence: "high" | "medium";
  }>;
  search_queries: string[];
  person_researched_at: string | null;
  outreach_angle: string | null;
  caveats: string[];
  sources: ContactSource[];
  outreach_email: OutreachEmail | null;
};

/** Messages d'approche propres à un interlocuteur, rédigés par `lib/lead-email.ts`. */
export type OutreachEmail = {
  subject: string;
  /** Formule d'appel comprise, sans signature. */
  body: string;
  /** Relance dans le même fil, quelques jours plus tard. */
  follow_up: string | null;
  /** Version pour la messagerie LinkedIn, sans objet. */
  linkedin?: string | null;
  /** Note interne : l'angle retenu pour cette personne. */
  angle: string | null;
  /** Sources des faits cités, à garder sous la main si le destinataire demande. */
  sources: string[];
  word_count: number;
  /** Language of the messages (the contact's); absent on drafts written before it was recorded. */
  language?: "fr" | "en";
  written_at: string;
};

const PLACEHOLDER_TEXTS = new Set([
  "null",
  "undefined",
  "none",
  "n a",
  "na",
  "unknown",
  "inconnu",
  "inconnue",
  "not found",
  "not specified",
  "to be determined",
  "non trouve",
  "non trouvee",
  "non renseigne",
  "non renseignee",
  "a identifier",
  "a determiner",
]);
const CONTACT_TITLE_WORDS = new Set(["dr", "docteur", "pr", "prof", "professeur", "m", "mr", "mme", "madame", "monsieur"]);

function normalizedPlaceholder(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Rejette les sentinelles textuelles parfois renvoyées par les modèles à la place de JSON null. */
export function isPlaceholderText(value: unknown) {
  return typeof value !== "string" || !value.trim() || PLACEHOLDER_TEXTS.has(normalizedPlaceholder(value));
}

/** Un contact affichable doit être une personne nommée, pas seulement une fonction cible. */
export function isUsableContactName(value: unknown): value is string {
  if (isPlaceholderText(value)) return false;
  const words = normalizedPlaceholder(value as string)
    .split(" ")
    .filter((word) => word.length >= 2 && !CONTACT_TITLE_WORDS.has(word));
  return words.length >= 2;
}

/** Filtre les faux profils historiques, puis recalcule un classement continu pour l’interface, le PDF et l’export. */
export function normalizeNamedContacts<T extends Pick<ContactCandidate, "contact_name" | "priority_rank" | "fit_score">>(
  contacts: readonly T[],
): T[] {
  return contacts
    .filter((contact) => isUsableContactName(contact.contact_name))
    .sort((a, b) => a.priority_rank - b.priority_rank || (b.fit_score ?? 0) - (a.fit_score ?? 0))
    .map((contact, index) => ({ ...contact, priority_rank: index + 1 }));
}

/**
 * Message d'avant les e-mails par interlocuteur : l'accroche du contact et le
 * brouillon commun du signal, réunis en un seul texte à une seule formule d'appel.
 */
export function legacyMessage(base: string | null | undefined, contact: Pick<ContactCandidate, "icebreakers"> | null) {
  const greetingPattern = /^(?:bonjour|hello|hi)\b[^,\n]*,?\s*/i;
  let opener = contact?.icebreakers?.[0]?.text?.trim();
  let body = base?.trim();
  if (!opener || !body || body.toLocaleLowerCase("fr").includes(opener.toLocaleLowerCase("fr").slice(0, 48))) {
    return body || opener || "";
  }
  // L'accroche ouvre déjà le message : la formule d'appel du brouillon remonte
  // au-dessus d'elle, ou disparaît si l'accroche a la sienne.
  const greeting = body.match(greetingPattern)?.[0].trim();
  body = body.replace(greetingPattern, "");
  body = body.charAt(0).toLocaleUpperCase("fr") + body.slice(1);
  if (greeting && !greetingPattern.test(opener)) opener = `${greeting}\n\n${opener}`;
  return `${opener}\n\n${body}`;
}

/**
 * Texte prêt à coller : l'e-mail rédigé pour ce contact, à défaut l'ancien
 * message. La fiche lead le copie, l'export CSV l'écrit tel quel.
 */
export function contactMessage(
  base: string | null | undefined,
  contact: Pick<ContactCandidate, "icebreakers" | "outreach_email"> | null,
) {
  return contact?.outreach_email?.body || legacyMessage(base, contact);
}

/**
 * Recherche de l'écran des leads : le nom, la catégorie ou le dernier signal
 * contiennent le texte saisi, casse ignorée. L'export CSV applique la même.
 */
export function leadMatchesSearch(
  lead: { name: string; category: string | null; latest_signal: { market_clue: string } | null },
  search: string,
) {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    lead.name.toLowerCase().includes(needle) ||
    (lead.category ?? "").toLowerCase().includes(needle) ||
    (lead.latest_signal?.market_clue ?? "").toLowerCase().includes(needle)
  );
}

export type LeadOsintSignal = {
  type:
    | "vendor_echo"
    | "event"
    | "hiring"
    | "transformation"
    | "public_document"
    | "legal_event"
    | "procurement"
    | "advertising"
    | "technology"
    | "public_voice"
    | "customer_pain"
    | "other";
  title: string;
  fact: string;
  commercial_implication: string;
  date: string | null;
  source_url: string;
  confidence: "high" | "medium";
};

export type LeadOsintQuestion = {
  question: string;
  rationale: string;
  source_url: string;
};

/**
 * Réponse de l'enquête à un critère de qualification de l'offre. Confirmé :
 * une source directe l'établit ; déduit : la source est indirecte ou
 * ancienne ; inconnu : rien de public, et `question` dit quoi demander.
 */
export type LeadKeyFact = {
  criterion: string;
  decisive: boolean;
  status: "confirmed" | "inferred" | "unknown";
  /** Réponse courte (« Doctolib », « 2 sites »), absente si inconnu. */
  value: string | null;
  detail: string | null;
  source_url: string | null;
  question: string | null;
};

export type LeadOsintScoreBreakdown = {
  pain_strength: number;
  product_fit: number;
  timing: number;
  public_reachability: number;
  evidence_quality: number;
};

export type Lead = {
  id: string;
  user_id: string;
  name: string;
  category: string | null;
  status: LeadStatus;
  assigned_to: string | null;
  favorite: boolean;
  archived_at: string | null;
  opened_at: string | null;
  dismiss_reason: DismissReason | null;
  dismiss_note: string | null;
  dismissed_at: string | null;
  website: string | null;
  linkedin_url: string | null;
  contact_role: string | null;
  contact_name: string | null;
  contact_linkedin_url: string | null;
  contact_reason: string | null;
  contact_sources: ContactSource[] | null;
  contact_research_context: string | null;
  contact_research_gaps: string[] | null;
  contacts: ContactCandidate[];
  osint_summary: string | null;
  osint_score: number | null;
  osint_stage: "high_intent" | "problem_aware" | "trigger_present" | "potential_fit" | null;
  osint_score_breakdown: LeadOsintScoreBreakdown | null;
  osint_signals: LeadOsintSignal[];
  osint_key_facts: LeadKeyFact[];
  osint_questions: LeadOsintQuestion[];
  osint_search_queries: string[];
  osint_gaps: string[];
  osint_researched_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  reactivated_at: string | null;
  signal_count: number;
  latest_signal: LeadSignal | null;
};

export type TeamMember = { user_id: string; label: string };
